import {
  isInlineAbstractStepEnabled,
  isPostPaymentAbstractEnabled,
  POST_PAYMENT_ABSTRACT_FORM_STEP,
} from "./registrationModules";

const CONTACT_FIELDS = ["email", "mobile", "firstName", "lastName", "country"];

export const validateEmailAdvanced = (email) => {
  if (!email) return "Email is required.";

  const emailPattern =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  if (!emailPattern.test(email)) {
    return "Please enter a valid email address format.";
  }

  const disposableDomains = [
    "10minutemail.com",
    "tempmail.org",
    "guerrillamail.com",
    "mailinator.com",
    "trashmail.com",
    "0-mail.com",
    "throwaway.email",
    "temp-mail.org",
    "yopmail.com",
    "emailondeck.com",
    "tempail.com",
    "sharklasers.com",
  ];

  const domain = email.split("@")[1]?.toLowerCase();
  if (disposableDomains.includes(domain)) {
    return "Please use a permanent email address, not a temporary one.";
  }

  if (domain) {
    const domainParts = domain.split(".");
    if (domainParts.length < 2) {
      return "Email domain must have a valid top-level domain.";
    }

    const tld = domainParts[domainParts.length - 1];
    if (tld.length < 2 || tld.length > 6) {
      return "Email domain has an invalid top-level domain.";
    }

    if (domain.includes("..")) {
      return "Email address cannot contain consecutive dots.";
    }
  }

  if (email.length > 254) {
    return "Email address is too long.";
  }

  const localPart = email.split("@")[0];
  if (localPart.length > 64) {
    return "Email address local part is too long.";
  }

  return "";
};

export const validateMobileAdvanced = (mobile, country) => {
  const normalized = mobile.replace(/[\s()-+]/g, "");
  if (!normalized) return "Mobile number is required.";

  if (!/^\d+$/.test(normalized)) {
    return "Mobile number can only contain digits.";
  }

  const isSequential = (num) => {
    for (let i = 1; i < num.length; i++) {
      if (parseInt(num[i]) !== parseInt(num[i - 1]) + 1) return false;
    }
    return true;
  };

  const isRepeated = (num) => num.split("").every((digit) => digit === num[0]);

  if (isRepeated(normalized) || isSequential(normalized)) {
    return "Please enter a valid mobile number.";
  }

  if (country === "India") {
    const indianNumber = normalized.replace(/^(?:91|0)/, "");

    if (indianNumber.length !== 10) {
      return "Indian mobile number must be 10 digits.";
    }

    if (!/^[6-9]/.test(indianNumber)) {
      return "Indian mobile number must start with 6, 7, 8, or 9.";
    }

    const validPrefixes = [
      "60",
      "61",
      "62",
      "63",
      "64",
      "65",
      "66",
      "67",
      "68",
      "69",
      "70",
      "71",
      "72",
      "73",
      "74",
      "75",
      "76",
      "77",
      "78",
      "79",
      "80",
      "81",
      "82",
      "83",
      "84",
      "85",
      "86",
      "87",
      "88",
      "89",
      "90",
      "91",
      "92",
      "93",
      "94",
      "95",
      "96",
      "97",
      "98",
      "99",
    ];

    const prefix = indianNumber.substring(0, 2);
    if (!validPrefixes.includes(prefix)) {
      return "Please enter a valid Indian mobile number.";
    }
  } else if (country === "United States") {
    const usNumber = normalized.replace(/^1/, "");

    if (usNumber.length !== 10) {
      return "US mobile number must be 10 digits.";
    }

    if (/^[01]/.test(usNumber)) {
      return "US phone number area code cannot start with 0 or 1.";
    }

    const exchange = usNumber.substring(3, 6);
    if (/^[01]/.test(exchange)) {
      return "US phone number exchange code cannot start with 0 or 1.";
    }
  } else if (country === "United Kingdom") {
    const ukNumber = normalized.replace(/^44/, "");

    if (!ukNumber.startsWith("07") || ukNumber.length !== 11) {
      return "UK mobile number must start with 07 and be 11 digits.";
    }
  } else if (country === "Australia") {
    const auNumber = normalized.replace(/^61/, "");

    if (!auNumber.startsWith("04") || auNumber.length !== 10) {
      return "Australian mobile number must start with 04 and be 10 digits.";
    }
  } else if (country === "Canada") {
    const caNumber = normalized.replace(/^1/, "");

    if (caNumber.length !== 10) {
      return "Canadian mobile number must be 10 digits.";
    }

    if (/^[01]/.test(caNumber)) {
      return "Canadian phone number area code cannot start with 0 or 1.";
    }
  } else {
    if (normalized.length < 7 || normalized.length > 15) {
      return "Mobile number must be between 7 and 15 digits.";
    }

    if (normalized.startsWith("00")) {
      return "Please enter the mobile number without international dialing codes.";
    }
  }

  return "";
};

export const validateContactField = (field, rawValue, overrides = {}) => {
  if (!CONTACT_FIELDS.includes(field)) {
    return "";
  }

  const valueSource = overrides.value ?? rawValue;
  const value = typeof valueSource === "string" ? valueSource.trim() : "";
  const country = overrides.country ?? "";

  if (field === "firstName") {
    if (!value) return "First name is required.";
    if (value.length < 2) return "First name must be at least 2 characters.";
    if (!/^[a-zA-Z\s.'-]+$/.test(value)) {
      return "First name can only contain letters, spaces, dots, apostrophes, and hyphens.";
    }
  }

  if (field === "lastName") {
    if (!value) return "Last name is required.";
    if (value.length < 1) return "Last name must be at least 2 characters.";
    if (!/^[a-zA-Z\s.'-]+$/.test(value)) {
      return "Last name can only contain letters, spaces, dots, apostrophes, and hyphens.";
    }
  }

  if (field === "country" && !value) {
    return "Country is required.";
  }

  if (field === "email") {
    return validateEmailAdvanced(value);
  }

  if (field === "mobile") {
    return validateMobileAdvanced(value, country);
  }

  return "";
};

export const validateBasicSaveContact = ({ formData, validateField }) => {
  const firstNameValid = validateField("firstName", formData.firstName);
  const lastNameValid = validateField("lastName", formData.lastName);
  const emailValid = validateField("email", formData.email);
  let mobileValid = true;
  let mobileError = null;

  if (formData.mobile && formData.mobile.trim()) {
    if (formData.country) {
      mobileValid = validateField("mobile", formData.mobile, {
        country: formData.country,
        value: formData.mobile,
      });
    } else {
      const normalizedMobile = formData.mobile.replace(/[\s()-+]/g, "");
      if (!normalizedMobile) {
        mobileError = "Mobile number is required";
        mobileValid = false;
      } else if (
        normalizedMobile.length < 7 ||
        normalizedMobile.length > 15 ||
        !/^\d+$/.test(normalizedMobile)
      ) {
        mobileError = "Please enter a valid mobile number (7-15 digits)";
        mobileValid = false;
      } else {
        mobileError = "";
        mobileValid = true;
      }
    }
  } else {
    mobileError = "Mobile number is required";
    mobileValid = false;
  }

  return {
    isValid: firstNameValid && lastNameValid && emailValid && mobileValid,
    mobileError,
  };
};

export const validateRegistrationStep = ({
  currentStep,
  formData,
  genderOptions,
  abstractStepNumber,
  wantsToSubmitAbstract,
  paperTitle,
  tracks,
  selectedTrack,
  presentationMode,
  submissionMethod,
  abstractText,
  maxAbstractChars,
  paperFile,
  paperFileUploaded,
  categories,
  registrationTypes,
  selectedComboWorkshop,
  accompanyingPersons,
}) => {
  const requiredFields = [];
  let studentIdCardError = null;
  let abstractCharLimitError = null;

  if (currentStep === 1) {
    if (!formData.title || !formData.title.trim()) requiredFields.push("Title");
    if (!formData.firstName.trim()) requiredFields.push("First Name");
    if (!formData.lastName.trim()) requiredFields.push("Last Name");
    if (!formData.email.trim()) requiredFields.push("Email");
    if (!formData.mobile.trim()) requiredFields.push("Mobile");
    if (
      genderOptions.length > 0 &&
      (!formData.gender || !formData.gender.trim())
    ) {
      requiredFields.push("Gender");
    }
  }

  if (currentStep === 2) {
    if (!formData.country.trim()) requiredFields.push("Country");
    if (!formData.city.trim()) requiredFields.push("City");
    if (!formData.pincode.trim()) requiredFields.push("Pincode");
    if (!formData.address.trim()) requiredFields.push("Address");
  }

  if (currentStep === 3 && isInlineAbstractStepEnabled(abstractStepNumber)) {
    console.log("🔍 Step 3 Abstract Validation:", {
      abstractStepNumber,
      wantsToSubmitAbstract,
    });

    if (wantsToSubmitAbstract === null) {
      console.log(
        "❌ Step 3 validation failed - user hasn't chosen abstract vs delegate",
      );
      requiredFields.push(
        "Please select whether you want to submit an abstract or register as a delegate",
      );
    } else if (wantsToSubmitAbstract === true) {
      if (!paperTitle.trim()) requiredFields.push("Paper Title");
      if (tracks.length > 0 && !selectedTrack) requiredFields.push("Track");
      if (!presentationMode) requiredFields.push("Presentation Mode");
      if (!submissionMethod) requiredFields.push("Submission Method");

      if (submissionMethod === "TEXTAREA" || submissionMethod === "BOTH") {
        if (!abstractText.trim()) requiredFields.push("Structured Abstract");
        if (abstractText.length > maxAbstractChars) {
          abstractCharLimitError = `Structured Abstract must not exceed ${maxAbstractChars} characters. Current character count: ${abstractText.length}`;
        }
      }

      if (submissionMethod === "FILE" || submissionMethod === "BOTH") {
        if (!paperFile && !paperFileUploaded) requiredFields.push("Paper File");
      }
    }
  }

  if (
    currentStep === POST_PAYMENT_ABSTRACT_FORM_STEP &&
    isPostPaymentAbstractEnabled(abstractStepNumber)
  ) {
    console.log("🔍 Post-payment abstract validation:", {
      abstractStepNumber,
      paid: formData.paid,
    });

    if (!paperTitle.trim()) requiredFields.push("Paper Title");
    if (tracks.length > 0 && !selectedTrack) requiredFields.push("Track");
    if (!presentationMode) requiredFields.push("Presentation Mode");
    if (!submissionMethod) requiredFields.push("Submission Method");

    if (submissionMethod === "TEXTAREA" || submissionMethod === "BOTH") {
      if (!abstractText.trim()) requiredFields.push("Structured Abstract");
      if (abstractText.length > maxAbstractChars) {
        abstractCharLimitError = `Structured Abstract must not exceed ${maxAbstractChars} characters. Current character count: ${abstractText.length}`;
      }
    }

    if (submissionMethod === "FILE" || submissionMethod === "BOTH") {
      if (!paperFile && !paperFileUploaded) requiredFields.push("Paper File");
    }
  }

  if (currentStep === 4) {
    if (!formData.affiliation.trim()) requiredFields.push("Affiliation");
    if (!formData.institution.trim()) {
      requiredFields.push("Institution or Hospital Name");
    }
    if (formData.mciRegistered === "Yes" && !formData.mciNumber.trim()) {
      requiredFields.push("MCI Number");
    }
  }

  if (currentStep === 5) {
    if (
      categories.length > 0 &&
      (!formData.category ||
        !String(formData.category).trim() ||
        ["select category", "tbd"].includes(
          String(formData.category).trim().toLowerCase(),
        ))
    ) {
      requiredFields.push("Category");
    }
    if (
      registrationTypes.length > 0 &&
      (!formData.registrationType.trim() ||
        ["select registrationType", "tbd"].includes(
          String(formData.registrationType).trim().toLowerCase(),
        ))
    ) {
      requiredFields.push("Registration Type");
    }
    if (
      formData.category === "ISCCM(Members)" &&
      !formData.membershipNumber.trim()
    ) {
      requiredFields.push("Membership Number");
    }
    if (
      formData.category &&
      formData.category.toString().toLowerCase().includes("isa") &&
      !formData.category.toString().toLowerCase().includes("non") &&
      !formData.category.toString().toLowerCase().includes("isccm") &&
      (!formData.isaMembershipNumber || !formData.isaMembershipNumber.trim())
    ) {
      requiredFields.push("ISA Membership Number");
    }

    const isStudentCategory =
      formData.category && formData.category.toLowerCase().includes("student");
    const isStudentRegistrationType =
      formData.registrationType &&
      formData.registrationType.toLowerCase().includes("student");

    console.log("🎓 Student validation check:");
    console.log("  Category:", formData.category);
    console.log("  Registration Type:", formData.registrationType);
    console.log("  isStudentCategory:", isStudentCategory);
    console.log("  isStudentRegistrationType:", isStudentRegistrationType);
    console.log("  formData.studentIdCard:", formData.studentIdCard);
    console.log("  studentIdCard type:", typeof formData.studentIdCard);

    const isStudentRegistration =
      isStudentCategory || isStudentRegistrationType;
    const hasValidStudentIdCard =
      formData.studentIdCard &&
      formData.studentIdCard !== null &&
      formData.studentIdCard !== "" &&
      (formData.studentIdCard.startsWith("data:") ||
        formData.studentIdCard === "DATABASE" ||
        formData.studentIdCard.startsWith("http") ||
        formData.studentIdCard.includes("/"));

    console.log("  hasValidStudentIdCard:", hasValidStudentIdCard);

    if (isStudentRegistration && !hasValidStudentIdCard) {
      console.log(
        "  ❌ VALIDATION FAILED: Student ID Card is required but missing!",
      );
      requiredFields.push("Student ID Card");
      studentIdCardError =
        "Student ID Card is required for student registrations";
    } else {
      console.log(
        isStudentRegistration
          ? "  ✅ Student ID Card validation passed - file present"
          : "  ✅ Not a student registration - clearing error",
      );
      studentIdCardError = "";
    }

    const isRegistrationTypeCombo =
      formData.registrationType &&
      formData.registrationType.toLowerCase().includes("workshop");


    if (isRegistrationTypeCombo) {
      if (!selectedComboWorkshop) {
        requiredFields.push("Select Workshop");
      }
    }

    accompanyingPersons.forEach((person, index) => {
      if (!person.name || !person.name.trim()) {
        requiredFields.push(`Accompanying Person ${index + 1} Name`);
      }
    });
  }

  return {
    isValid: requiredFields.length === 0 && !abstractCharLimitError,
    requiredFields,
    studentIdCardError,
    abstractCharLimitError,
  };
};
